<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="pb-2 flex justify-between items-end border-b border-brand-grayblue-300">
      <span class="text-xl md:text-3xl">{{ $t('estabSubUsersManagment') }}</span>
    </h1>
    <InfoBar :text="$t('estabSubUsersManagmentHint')"></InfoBar>
    <div class="w-full flex justify-end">
      <RouterLink
        :to="{ name: 'EstUserManagementNew' }"
        class="pe-1.5 rounded-md bg-primary text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
      >
        <div class="flex justify-center items-center h-full p-2 rounded-s bg-black/10">
          <faUserPlus class="w-5 fill-white" />
        </div>
        <span class="px-2 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
          {{ $t('addNewUserAccount') }}
        </span>
      </RouterLink>
    </div>
    <SearchForm @on-search="handleSearch"></SearchForm>
    <FeesTableSkeleton v-if="isLoading"></FeesTableSkeleton>
    <template v-else>
      <DataTable
        v-if="dataSet"
        :default-headers="['name', 'email', 'identityNumber', 'actions']"
        :data-set="dataSet"
      >
        <template #headerCell="{ label }">
          <span v-if="label == 'actions'"></span>
        </template>
        <template #cell="{ columnName, columnValue, rowIndex }">
          <span v-if="columnName == 'name'" v-html="columnValue"></span>
          <div v-if="columnName == 'actions'" class="w-full h-full flex gap-1">
            <tippy
              v-if="hasAction(rowIndex)"
              :theme="useThemeStore().currentScheme == 'light' ? 'light-border' : 'dark-border'"
              :arrow="false"
              :offset="[0, 4]"
              interactive
              trigger="click"
              v-bind="{ ...(maxDate && { maxDate: new Date() }) }"
              placement="bottom-end"
            >
              <button class="rounded p-2 bg-gray-600" @click.prevent="">
                <faBars class="w-3 fill-white" />
              </button>
              <template #content>
                <div class="flex flex-col gap-y-2 max-h-[12rem] overflow-y-auto">
                  <RouterLink
                    v-if="canUpdateProfile(users[rowIndex])"
                    :to="{
                      name: 'EstUserManagementUpdate',
                      params: { email: users[rowIndex]['email'] }
                    }"
                    class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                    @click="editSubUser(users[rowIndex])"
                  >
                    {{ $t('updateUserProfileInfo') }}
                  </RouterLink>
                  <button
                    v-if="canViewActivationCode(users[rowIndex])"
                    class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                    @click="viewActivationCode(users[rowIndex])"
                  >
                    {{ $t('viewProfileActivationCode') }}
                  </button>
                  <button
                    v-if="canActivate(users[rowIndex])"
                    class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                    @click="activateUser(users[rowIndex])"
                  >
                    {{ $t('activate') }}
                  </button>
                  <button
                    v-if="canDeactivate(users[rowIndex])"
                    class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                    @click="deactivateUser(users[rowIndex])"
                  >
                    {{ $t('deactivate') }}
                  </button>
                </div>
              </template>
            </tippy>
          </div>
        </template>
      </DataTable>
      <!-- <div
        v-if="dataSet.length > 5"
        class="py-2 w-full flex flex-col md:flex-row gap-y-4 items-center justify-center md:justify-between"
      >
        <div class="w-56 flex border border-brand-gray-300 rounded-md">
          <button
            :key="pc"
            v-for="pc in pageCounts"
            :class="{ 'bg-primary text-white': paginatorConfig.itemsPerPage == pc }"
            class="flex-1 p-1 border-e first:rounded-s-md last:rounded-e-md last:border-none border-brand-gray-300 hover:bg-primary/70"
            @click="onPageCountChange(pc)"
          >
            {{ pc }}
          </button>
        </div>
        <Paginate
          :page-count="paginatorConfig.pageCount"
          :page-range="paginatorConfig.itemsPerPage"
          :click-handler="onPageClick"
          :prev-text="'‹'"
          :next-text="'›'"
          :first-last-button="true"
          v-model="paginatorConfig.currentPage"
          container-class="pagination"
          first-button-text="«"
          last-button-text="»"
          class="accent-primary-700"
        />
        <div class="w-56 flex justify-center md:justify-end items-center gap-x-2">
          <input
            type="text"
            class="w-full max-w-[4ch] py-1.5 rounded-md border border-brand-gray-600 outline-none text-center focus:border-primary"
            v-model.number="paginatorConfig.currentPage"
            @input="onOverridePage(paginatorConfig.currentPage)"
          />
          <span class="">{{ $t('of') }} {{ paginatorConfig.pageCount }}</span>
        </div>
      </div> -->
    </template>
  </div>
</template>

<script setup>
import faBars from '@/assets/icons/faBars.svg?component'
import faUserPlus from '@/assets/icons/faUserPlus.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import DataTable from '@/components/DataTable/index.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import axios from '@/services'
import { useEstablishmentStore } from '@/stores/establishment'
import { useThemeStore } from '@/stores/theme'
import SearchForm from '@/views/establishment/user-management/landing/SearchForm.vue'
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Dev Tip:
 * paginator logic is commented out.
 * once api response includes appriopriate data,
 * simply adjust users.value = data.map((d) => {...})
 * and uncomment relevant lines.
 */

const estStore = useEstablishmentStore()
const i18n = useI18n()

const isLoading = ref(true)
const users = ref()

// const pageCounts = [5, 10, 20, 50, 100]

const searchCriteria = reactive({
  name: null,
  email: null,
  eidaNumber: null,
  hasidentityNumberFlag: false,
  currentPage: 1,
  pageSize: 10
})

// const paginatorConfig = reactive({
//   totalItemCount: 0,
//   itemsPerPage: 10,
//   currentPage: 1,
//   pageCount: 0
// })

const {
  activateDeactivate: { activate, deactivate }
} = client_lookups

const dataSet = computed(() => {
  if ((users.value || []).length === 0) {
    return null
  }

  return users.value.map((u) => {
    const row = {}

    if (estStore.canToggleUserProfile) {
      // row.accountStatus =
      switch (u.userStatusId) {
        case activate:
          row.accountStatus = i18n.t('activated')
          break
        case deactivate:
          row.accountStatus = i18n.t('deactivated')
          break
        default:
          break
      }
    }

    Object.assign(row, {
      name: `<div class="flex flex-col gap-y-2"><span>${u.arabicFullName}</span><span>${u.englishFullName}</span></div>`,
      email: u.email,
      identityNumber: u.identityNumber || i18n.t('NA'),
      actions: ''
    })

    return row
  })
})

onMounted(async () => {
  await estStore.getUAEPassSetup()
  await getEstablishmentUsers(searchCriteria)
  isLoading.value = false
})

const handleSearch = async (searchCriteria) => {
  await getEstablishmentUsers(searchCriteria)
}

async function getEstablishmentUsers(searchCriteria = {}) {
  isLoading.value = true

  const data = await axios.post('/api/userProfile/searchUserProfile', {
    ...searchCriteria,
    hasidentityNumberFlag: searchCriteria.hasidentityNumberFlag ? 1 : 0
  })

  users.value = data.map((d) => {
    return {
      ...d,
      isActive: d.userStatusId == activate
    }
  })

  // paginatorConfig.totalItemCount = numberOfRecords
  // paginatorConfig.pageCount = Math.ceil((numberOfRecords || 1) / paginatorConfig.itemsPerPage)
}

const canUpdateProfile = (user) => {
  const { enableUpdateMainUserProfileEmiratesId } = estStore.uaePassConfig
  const { identityNumber, isMainUserFlag, isActive } = user
  const hasIdentity = !!identityNumber
  const isMainProfileAndCanUpdate = isMainUserFlag && enableUpdateMainUserProfileEmiratesId
  return (hasIdentity || isMainProfileAndCanUpdate) && isActive
}

const canViewActivationCode = (user) => {
  const { registerUserCatToken, isMainUserFlag, isActive } = user
  return registerUserCatToken && !isMainUserFlag && isActive
}

const canActivate = (user) => {
  const { activateAndDeactivateUserProfiles } = estStore.uaePassConfig
  const { isMainUserFlag, isActive } = user

  return !isActive && !isMainUserFlag && activateAndDeactivateUserProfiles
}

const canDeactivate = (user) => {
  const { activateAndDeactivateUserProfiles } = estStore.uaePassConfig
  const { isMainUserFlag, isActive } = user

  return isActive && !isMainUserFlag && activateAndDeactivateUserProfiles
}

const hasAction = (rowIndex) => {
  const user = users.value[rowIndex]

  const actions = {
    canUpdateProfile: canUpdateProfile(user),
    canViewActivationCode: canViewActivationCode(user),
    canActivate: canActivate(user),
    canDeactivate: canDeactivate(user)
  }

  return Object.values(actions).some((a) => !!a)
}

const editSubUser = async (user) => {
  console.log(`editSubUser called for`, user)
}
const viewActivationCode = async (user) => {
  console.log(`viewActivationCOde called for`, user)
}
const activateUser = async (user) => {
  console.log(`activateUser called for`, user)
}
const deactivateUser = async (user) => {
  console.log(`deactivateUser called for`, user)
}
</script>

<script>
export default {
  name: 'EstRelatedUsers'
}
</script>

<style lang="scss" scoped></style>
